import React, { useRef } from "react";
import { Correct, Pause, Play, Wrong } from "@/components/Icon";
import "./styles.scss";
import { Message, Option, QuestionContent } from "./index";
import { VideoRenderer } from "./RenderVideo";

interface AudioState {
  isPlaying?: boolean | undefined;
  position?: number | undefined;
  duration?: number | undefined;
  isAudioLoading?: boolean | undefined;
  audioError?: string | null | undefined;
}

interface MessageRendererProps {
  item: Message;
  audioStates: Record<string, AudioState>;
  setAudioStates: React.Dispatch<
    React.SetStateAction<Record<string, AudioState>>
  >;
  waveformData: Record<string, number[]>;
  loadAudio: (url: string, id: string) => void;
  toggleAudio: (id: string) => void;
  handleRetry: () => void;
  waveformBars: number;
}

export const MessageRenderer: React.FC<MessageRendererProps> = ({
  item,
  audioStates,
  setAudioStates,
  waveformData,
  loadAudio,
  toggleAudio,
  handleRetry,
  waveformBars,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const formatTime = (millis: number) => {
    const seconds = Math.floor(millis / 1000);
    return `${Math.floor(seconds / 60)}:${(seconds % 60)
      .toString()
      .padStart(2, "0")}`;
  };

  const renderAudioVisual = (messageId: string, audioUrl: string) => {
    if (!audioStates[messageId]) {
      setAudioStates((prev) => ({
        ...prev,
        [messageId]: {
          isPlaying: false,
          position: 0,
          duration: 0,
          isAudioLoading: false,
          audioError: null,
        },
      }));
      if (audioUrl) {
        loadAudio(audioUrl, messageId);
      }
    }

    const {
      isPlaying = false,
      position = 0,
      duration = 0,
      isAudioLoading = false,
      audioError = null,
    } = audioStates[messageId] || {};

    const progress = duration > 0 ? position / duration : 0;
    const activeBars = Math.floor(progress * waveformBars);

    return (
      <div className="voiceNoteContainer">
        <div className="audio-row">
          <button
            onClick={() => toggleAudio(messageId)}
            className="playButton"
            disabled={isAudioLoading}
          >
            {isAudioLoading ? (
              <div className="spinner small" />
            ) : isPlaying ? (
              <Pause />
            ) : (
              <Play />
            )}
          </button>
          <div className="waveformContainer">
            <div className="waveform">
              {(waveformData[messageId] || []).map((height, i) => (
                <div
                  key={i}
                  className="waveBar"
                  style={{
                    height: i <= activeBars ? height : height * 0.5,
                    backgroundColor: i <= activeBars ? "#075E54" : "#B0B0B0",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        {/* {audioError && (
          <p className="errorText">Error loading audio: {audioError}</p>
        )} */}
        <p className="timeText">{formatTime(position)}</p>
      </div>
    );
  };

  const renderImage = (imageUrl: string) => (
    <div className="imageContainer">
      <img src={imageUrl} alt="message" className="image" />
    </div>
  );

  if (item.type === "question") {
    const content = item.content as QuestionContent;
    return (
      <div className="questionBubble">
        {content.audioUrl ? (
          renderAudioVisual(`${item.type}-${item.id}`, content.audioUrl)
        ) : content.videoUrl ? (
          <VideoRenderer
            messageId={`${item.type}-${item.id}`}
            videoUrl={content.videoUrl}
          />
        ) : content.imageUrl ? (
          renderImage(content.imageUrl)
        ) : (
          <p className="questionText">{content.text}</p>
        )}
      </div>
    );
  }

  if (item.type === "answer") {
    return (
      <div className="answerBubble">
        {item.content.map((option: Option, index: number) => (
          <p key={index} className="answerText">
            {option.text}
          </p>
        ))}
        {item.correct ? (
          <span className="correctMark">
            <Correct />
          </span>
        ) : (
          <button className="retryButton" onClick={handleRetry}>
            <Wrong /> Try again
          </button>
        )}
      </div>
    );
  }

  return null;
};
